import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import { POST_LOGIN_REDIRECT_KEY } from '@/constants/auth';
import { PATH_URL } from '@/constants/path-constants';
import { execWithWindow } from '@/features/left-nav/Misc/window';
import { getPartialLoginState } from '@/shared/utils/auth';
import {
  buildOutlookAddinLoginUrl,
  isOutlookAddinSigninFlow,
} from '@/shared/utils/outlookAddinSignin';
import {
  getSessionItem,
  removeSessionItem,
} from '@/shared/utils/sessionStorageUtils';

type QueryParamsHandling = '' | 'merge' | 'preserve';

type GoToSignedInAppOptions = {
  queryParamsHandling?: QueryParamsHandling;
  shouldRefreshPage?: boolean;
  // twoFactorSettings?: TwoFactorSettings;
  isLinkedIn?: boolean;
  skipRedirect?: boolean;
  emailVerifyRequired?: boolean;
};

type GoTo2FACodeOptions = {
  redirectUrl: string;
  shouldRefreshPage?: boolean;
};

export function useAuthNavigation() {
  const router = useRouter();
  const searchParams = useSearchParams();

  // todo(hao): add 2FA setup
  const goTo2FACode = useCallback(
    ({ redirectUrl, shouldRefreshPage = false }: GoTo2FACodeOptions) => {
      const twoFactorSettings = getPartialLoginState().twoFactorSettings;

      const redirectQueryParams =
        getPartialLoginState().redirectQueryParams ?? {};

      const twoFactorUrl = twoFactorSettings?.two_factor_type
        ? PATH_URL.TWO_FACTOR
        : PATH_URL.TWO_FACTOR_SETUP;

      const url = new URL(`/${twoFactorUrl}`, window.location.origin);

      for (const [key, value] of searchParams.entries()) {
        url.searchParams.set(key, value);
      }

      for (const [key, value] of Object.entries(redirectQueryParams)) {
        url.searchParams.set(key, value);
      }

      if (twoFactorSettings?.two_factor_type) {
        url.searchParams.set('twofa', twoFactorSettings.two_factor_type);
      }

      url.searchParams.set('r', redirectUrl);

      if (shouldRefreshPage) {
        // replaceCurrentUrl(url.toString());
      } else {
        router.push(url.pathname + url.search);
      }
    },
    [router, searchParams],
  );

  // todo(hao): hanndle all other sign in cases (2fa, sso, shared speech)
  const goToSignedInApp = useCallback(
    ({ skipRedirect = false, emailVerifyRequired }: GoToSignedInAppOptions) => {
      const storedRedirect = getSessionItem<string>(
        POST_LOGIN_REDIRECT_KEY,
        undefined,
        { useRawKey: true },
      );
      removeSessionItem(POST_LOGIN_REDIRECT_KEY, { useRawKey: true });

      const redirectUrl: string =
        getPartialLoginState().redirectUrl ||
        searchParams.get('r') ||
        storedRedirect ||
        PATH_URL.HOME;
      const redirectQueryParams =
        getPartialLoginState().redirectQueryParams ?? {};

      const twoFactorSettings = getPartialLoginState().twoFactorSettings;

      const pathsNotRequireEmailVerify: string[] = [
        PATH_URL.WELCOME,
        PATH_URL.EMAIL_VERIFY,
        PATH_URL.PURCHASE_PRO_PLAN,
        PATH_URL.PURCHASE_TEAM_PLAN,
      ].map((p) => p.replace(/^\//, ''));

      if (
        twoFactorSettings?.two_factor_required ||
        twoFactorSettings?.two_factor_type
      ) {
        goTo2FACode({ redirectUrl });
      } else if (
        emailVerifyRequired &&
        !pathsNotRequireEmailVerify.includes(redirectUrl)
      ) {
        router.push(`/${PATH_URL.EMAIL_VERIFY}`);
      } else if (skipRedirect) {
        return;
      } else if (isOutlookAddinSigninFlow(searchParams)) {
        execWithWindow((win) => {
          win.location.href = buildOutlookAddinLoginUrl(searchParams);
        });
      } else if (
        redirectUrl.startsWith('otter://') ||
        redirectUrl.startsWith('otter-dev://')
      ) {
        execWithWindow((win) => {
          try {
            const deepLink = new URL(redirectUrl);
            for (const [key, value] of Object.entries(redirectQueryParams)) {
              deepLink.searchParams.set(key, value);
            }
            win.location.href = deepLink.toString();
          } catch {
            router.push(`/${PATH_URL.HOME}`);
          }
        });
      } else {
        const normalizedPath = redirectUrl.startsWith('/')
          ? redirectUrl
          : `/${redirectUrl}`;
        const url = new URL(normalizedPath, window.location.origin);

        // Copy all search params except 'r' from searchParams to url
        searchParams.forEach((value, key) => {
          if (key !== 'r') {
            url.searchParams.set(key, value);
          }
        });

        for (const [key, value] of Object.entries(redirectQueryParams)) {
          url.searchParams.set(key, value);
        }

        router.push(url.pathname + url.search);
      }
    },
    [goTo2FACode, router, searchParams],
  );

  return {
    goToSignedInApp,
    goTo2FACode,
  };
}
